import { useEffect, useState } from 'react';
import { ShieldCheck, ShieldX, ChevronDown, FileText, Image, Video, Mail, Mic, BookOpen, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Asset, Channel, AssetChannelPermission } from '../../types/assetRegistry';
import { AssetTypeBadge, BrandBadge } from '../../components/assetRegistry/Badges';

function AssetTypeIcon({ type }: { type: Asset['asset_type'] }) {
  const p = { size: 22, color: '#d1d5db' };
  switch (type) {
    case 'image': return <Image {...p} />;
    case 'video': return <Video {...p} />;
    case 'copy': return <FileText {...p} />;
    case 'email_template': return <Mail {...p} />;
    case 'podcast': return <Mic {...p} />;
    case 'training_module': return <BookOpen {...p} />;
    default: return <FileText {...p} />;
  }
}

interface BlockedAssetMeta {
  asset: Asset;
  reason: 'restricted' | 'exclusive_here' | 'exclusive_elsewhere';
  restrictionNote?: string | null;
  exclusiveChannelName?: string;
}

function blockReasonLabel(reason: BlockedAssetMeta['reason'], exclusiveChannelName?: string) {
  switch (reason) {
    case 'restricted':          return { label: 'Explicitly restricted from this channel', color: '#dc2626' };
    case 'exclusive_here':      return { label: 'Exclusive to this channel only — do not reuse elsewhere', color: '#d97706' };
    case 'exclusive_elsewhere': return { label: `Exclusive to ${exclusiveChannelName ?? 'another channel'} — blocked here`, color: '#dc2626' };
  }
}

function AssetRow({ asset, variant, reason, restrictionNote, exclusiveChannelName }: {
  asset: Asset; variant: 'approved' | 'blocked';
  reason?: BlockedAssetMeta['reason']; restrictionNote?: string | null; exclusiveChannelName?: string;
}) {
  const reasonMeta = reason ? blockReasonLabel(reason, exclusiveChannelName) : null;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', borderRadius: 10,
      background: variant === 'approved' ? '#f0fdf4' : '#fef2f2',
      border: `1px solid ${variant === 'approved' ? '#bbf7d0' : '#fecaca'}`,
    }}>
      <div style={{ width: 52, height: 38, borderRadius: 8, overflow: 'hidden', background: '#e5e7eb', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {asset.thumbnail_url
          ? <img src={asset.thumbnail_url} alt={asset.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          : <AssetTypeIcon type={asset.asset_type} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.title}</p>
        <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
          <AssetTypeBadge type={asset.asset_type} />
          <BrandBadge brand={asset.brand_entity} />
        </div>
        {reasonMeta && (
          <p style={{ margin: '5px 0 0', fontSize: 12, color: reasonMeta.color, display: 'flex', alignItems: 'center', gap: 4, fontStyle: 'italic' }}>
            <AlertTriangle size={11} />{reasonMeta.label}
          </p>
        )}
        {restrictionNote && <p style={{ margin: '3px 0 0', fontSize: 12, color: '#ef4444', fontStyle: 'italic' }}>Note: {restrictionNote}</p>}
      </div>
    </div>
  );
}

export function ChannelCheck() {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [allPermissions, setAllPermissions] = useState<AssetChannelPermission[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.from('channels').select('*').order('name').then(({ data }) => { if (data) setChannels(data as Channel[]); });
  }, []);

  useEffect(() => {
    if (!selectedChannel) return;
    setLoading(true);
    Promise.all([
      supabase.from('assets').select('*').eq('status', 'approved'),
      supabase.from('asset_channel_permissions').select('*'),
    ]).then(([assetsRes, permsRes]) => {
      if (assetsRes.data) setAssets(assetsRes.data as Asset[]);
      if (permsRes.data) setAllPermissions(permsRes.data as AssetChannelPermission[]);
      setLoading(false);
    });
  }, [selectedChannel]);

  const approvedAssets = assets.filter(a =>
    allPermissions.some(p => p.asset_id === a.id && p.channel_id === selectedChannel && p.permission_type === 'approved')
  );

  const blockedAssets: BlockedAssetMeta[] = assets.map(a => {
    const perm = allPermissions.find(p => p.asset_id === a.id && p.channel_id === selectedChannel);
    if (perm?.permission_type === 'restricted') return { asset: a, reason: 'restricted' as const, restrictionNote: perm.restriction_note };
    if (perm?.permission_type === 'exclusive') return { asset: a, reason: 'exclusive_here' as const, restrictionNote: perm.restriction_note };
    const excElsewhere = allPermissions.find(p => p.asset_id === a.id && p.permission_type === 'exclusive' && p.channel_id !== selectedChannel);
    if (excElsewhere) {
      const ch = channels.find(c => c.id === excElsewhere.channel_id);
      return { asset: a, reason: 'exclusive_elsewhere' as const, exclusiveChannelName: ch?.name?.replace(/_/g, ' ') ?? 'another channel' };
    }
    return null;
  }).filter(Boolean) as BlockedAssetMeta[];

  const selectedChannelData = channels.find(c => c.id === selectedChannel);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f7f9fb' }}>

      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <ShieldCheck size={22} color="#1B4B8A" />
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>Channel Safety Check</h1>
        </div>
        <p style={{ margin: '0 0 0 32px', fontSize: 13, color: '#6b7280' }}>
          Select a channel to see what assets are approved or blocked. Only <strong style={{ color: '#374151' }}>approved</strong> assets are shown.
        </p>
      </div>

      {/* Channel selector */}
      <div style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '16px 24px' }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Select Channel</label>
        <div style={{ position: 'relative', width: 320 }}>
          <select value={selectedChannel} onChange={e => setSelectedChannel(e.target.value)}
            style={{ width: '100%', appearance: 'none', padding: '10px 36px 10px 14px', fontSize: 13, fontWeight: 500, border: '1.5px solid #e5e7eb', borderRadius: 10, outline: 'none', background: '#fff', color: '#111827', cursor: 'pointer' }}
            onFocus={e => (e.target.style.borderColor = '#1B4B8A')} onBlur={e => (e.target.style.borderColor = '#e5e7eb')}>
            <option value="">Choose a channel...</option>
            {channels.map(c => <option key={c.id} value={c.id}>{c.name.replace(/_/g, ' ')}</option>)}
          </select>
          <ChevronDown size={16} color="#9ca3af" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
        </div>
        {selectedChannelData?.description && (
          <p style={{ margin: '8px 0 0', fontSize: 12, color: '#6b7280' }}>{selectedChannelData.description}</p>
        )}
      </div>

      {!selectedChannel ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center' }}>
            <ShieldCheck size={56} color="#e5e7eb" style={{ marginBottom: 16 }} />
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#9ca3af' }}>Select a channel to run the safety check</p>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#d1d5db' }}>This is your cross-purpose firewall</p>
          </div>
        </div>
      ) : loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 32, height: 32, border: '3px solid #1B4B8A', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {/* Legend */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Legend:</span>
            {[
              { color: '#dc2626', label: 'Restricted — explicitly blocked' },
              { color: '#d97706', label: 'Exclusive here — only for this channel' },
              { color: '#dc2626', label: 'Exclusive elsewhere — belongs to another channel' },
            ].map(({ color, label }) => (
              <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color }}>
                <AlertTriangle size={11} />{label}
              </span>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Approved */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <ShieldCheck size={16} color="#059669" />
                <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827' }}>Approved for this Channel</h2>
                <span style={{ marginLeft: 'auto', background: '#d1fae5', color: '#065f46', fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 20 }}>{approvedAssets.length}</span>
              </div>
              {approvedAssets.length === 0 ? (
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '24px 16px', textAlign: 'center' }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>No assets approved for this channel</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {approvedAssets.map(asset => <AssetRow key={asset.id} asset={asset} variant="approved" />)}
                </div>
              )}
            </div>

            {/* Blocked */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <ShieldX size={16} color="#dc2626" />
                <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827' }}>Blocked from this Channel</h2>
                <span style={{ marginLeft: 'auto', background: '#fee2e2', color: '#991b1b', fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 20 }}>{blockedAssets.length}</span>
              </div>
              {blockedAssets.length === 0 ? (
                <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '24px 16px', textAlign: 'center' }}>
                  <p style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>No assets blocked from this channel</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {blockedAssets.map(({ asset, reason, restrictionNote, exclusiveChannelName }) => (
                    <AssetRow key={asset.id} asset={asset} variant="blocked" reason={reason} restrictionNote={restrictionNote} exclusiveChannelName={exclusiveChannelName} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
